/**
 * Shared formatting helpers + palette for the TA Action Board.
 */
import type { ChainOption, GradedSlot, RoleBoard, TrackingKind } from '../../types/taActionBoard';

export const TZ = 'Europe/Berlin';

/** Approved design palette (tab badges, chips, active states). */
export const COLORS = {
  blueBg: '#DBEBFC',
  blueText: '#0C447C',
  orangeBg: '#FCEEE2',
  orangeText: '#854F0B',
  redBg: '#FCDFE4',
  redText: '#791F1F',
  greenBg: '#DEF5E6',
  greenText: '#085041',
  cotton: '#F3F3F3',
  // FINN status colors (brand palette) — the solid fills used for status dots,
  // centralized so every dot on the board reads from one place.
  statusGreen: '#26BC58',
  statusOrange: '#E88F3D',
  statusRed: '#E71D3F',
  statusBlue: '#0072EA',
  statusGray: '#9A9A9A',
} as const;

/**
 * Decision chip colors: red for Lever ratings 1-2, green for 3-4, mixed ranges
 * colored by their minimum ("🔴 mixed: 1-3" → red-leaning). Without a numeric
 * rating the chip shows "🟡 {consensus}" in yellow. The label itself arrives
 * server-normalized ("{dot} {n} - {Label}") — only the colors live here.
 */
export function ratingChipStyle(ratingLabel: string | null): { bg: string; text: string } {
  // A split panel ("mixed: a-b") is a REVIEW state, not a rejection — show it yellow.
  // Without this it matched the first digit (the minimum) and turned red, reading as
  // a no-hire on what is really "interviewers disagree, recruiter decides".
  if (/mixed/i.test(ratingLabel || '')) return { bg: COLORS.orangeBg, text: COLORS.orangeText };
  const digit = /[1-4]/.exec(ratingLabel || '');
  if (!digit) return { bg: COLORS.orangeBg, text: COLORS.orangeText }; // yellow fallback
  return Number(digit[0]) <= 2
    ? { bg: COLORS.redBg, text: COLORS.redText } // 1-2 → red
    : { bg: COLORS.greenBg, text: COLORS.greenText }; // 3-4 → green
}

export function firstNameFromEmail(email: string): string {
  const local = email.split('@')[0].split('.')[0] || email;
  return local.charAt(0).toUpperCase() + local.slice(1);
}

export function fullNameFromEmail(email: string): string {
  return email
    .split('@')[0]
    .split('.')
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(' ');
}

export function initials(name: string): string {
  return name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((w) => w[0].toUpperCase())
    .join('');
}

const dayFmt = new Intl.DateTimeFormat('en-US', {
  timeZone: TZ,
  weekday: 'short',
  month: 'short',
  day: '2-digit',
});
const timeFmt = new Intl.DateTimeFormat('en-GB', {
  timeZone: TZ,
  hour: '2-digit',
  minute: '2-digit',
  hour12: false,
});
const dateFmt = new Intl.DateTimeFormat('en-US', { timeZone: TZ, month: 'short', day: '2-digit' });
const clockFmt = new Intl.DateTimeFormat('en-GB', {
  timeZone: TZ,
  hour: '2-digit',
  minute: '2-digit',
  hour12: false,
});

export function fmtDate(iso: string | null): string {
  if (!iso) return '—';
  return dateFmt.format(new Date(iso));
}

/** ISO-ordered day key in the board's timezone — `en-CA` gives `2026-07-15`. */
const dayKeyFmt = new Intl.DateTimeFormat('en-CA', {
  timeZone: TZ,
  year: 'numeric',
  month: '2-digit',
  day: '2-digit',
});

/**
 * Do these two timestamps fall on the same day AS THE BOARD SHOWS THEM?
 *
 * In the board's timezone, never the browser's. Everything on these cards is formatted in
 * `Europe/Berlin`, so a comparison done in local time disagrees with the screen for anyone not
 * sitting in it: `21:30Z` and `23:30Z` on 15 July are one UTC day, but Berlin renders them as
 * 15 and 16 July — and the Feedback card would say "Feedback same day" above two different
 * dates (CodeRabbit on #668).
 *
 * The invariant to keep: this is true exactly when `fmtDate` prints the same thing for both.
 */
export function sameDisplayDay(a: string, b: string): boolean {
  return dayKeyFmt.format(new Date(a)) === dayKeyFmt.format(new Date(b));
}

export function fmtTime(iso: string): string {
  return clockFmt.format(new Date(iso));
}

/**
 * e.g. "Mon Jul 20 · 10:00" — a START only.
 *
 * For the places that know WHEN something begins but not how long it runs: a confirmed time read
 * back off the offer we sent (ReplyChip) carries a start, because that is what slot tracking
 * stores. Rendering a made-up end would be inventing the half we do not have.
 */
export function fmtSlotStart(start: string): string {
  const s = new Date(start);
  return `${dayFmt.format(s)} · ${timeFmt.format(s)}`;
}

/** e.g. "Mon Jul 20 · 10:00–11:30" */
export function fmtSlotRange(slot: Pick<GradedSlot, 'start' | 'end'>): string {
  const s = new Date(slot.start);
  const e = new Date(slot.end);
  return `${dayFmt.format(s)} · ${timeFmt.format(s)}–${timeFmt.format(e)}`;
}

/**
 * Short availability hint for a graded slot, used in compact pick lists
 * (e.g. the Mark-offered checkboxes): "1️⃣ Wed Jul 23 · 10:00–11:30 — both free".
 */
export function slotGradeHint(slot: Pick<GradedSlot, 'grade'>): string {
  if (slot.grade === 'free') return 'both free';
  if (slot.grade === 'needs-move') return 'needs a move';
  return 'with fallback';
}

/** Numbered slot prefixes — mirror the board's "top 3" framing. */
export const SLOT_EMOJI = ['1️⃣', '2️⃣', '3️⃣'];

export const slotNumber = (i: number): string => SLOT_EMOJI[i] ?? `${i + 1}.`;

const dayFmtDe = new Intl.DateTimeFormat('de-DE', {
  timeZone: TZ,
  weekday: 'short',
  day: 'numeric',
  month: 'long',
});

/**
 * German slot line for candidate-facing email drafts, e.g. "Mi 23. Juli, 10:00-11:30".
 * Plain hyphen on purpose — the drafts go out as plain text.
 */
export function fmtSlotRangeDe(slot: Pick<GradedSlot, 'start' | 'end'>): string {
  const s = new Date(slot.start);
  const e = new Date(slot.end);
  // "Mi., 23. Juli" → "Mi 23. Juli" (drop the abbreviation dot and the comma)
  const day = dayFmtDe.format(s).replace(/^(\p{L}+)\.?,?\s*/u, '$1 ');
  return `${day}, ${timeFmt.format(s)}-${timeFmt.format(e)}`;
}

/** Day header for grouped slot lists, e.g. "Mon Jul 20" */
export function fmtSlotDay(slot: Pick<GradedSlot, 'start'>): string {
  return dayFmt.format(new Date(slot.start));
}

export function roleOpenItems(role: RoleBoard): number {
  return (
    role.feedback.length +
    role.scheduling.candidates.length +
    (role.firstInterviews?.candidates.length ?? 0) +
    role.sla.actNow.length +
    role.sla.screeningOverSla
  );
}

/** "offered 3 days ago" / "offered today" — relative age of an offered slot. */
export function fmtDaysAgo(days: number | null, verb = 'offered'): string {
  if (days === null) return `${verb} — date unknown`;
  const whole = Math.round(days);
  if (whole <= 0) return `${verb} today`;
  return `${verb} ${whole} day${whole === 1 ? '' : 's'} ago`;
}

export function roleHasOpenItems(role: RoleBoard): boolean {
  return roleOpenItems(role) > 0;
}

/** "just now" / "3 min ago" / "2 h ago" / "yesterday" / "3 days ago" */
export function fmtRelativeTime(date: Date, now: Date = new Date()): string {
  const mins = Math.max(0, Math.round((now.getTime() - date.getTime()) / 60_000));
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins} min ago`;
  const hours = Math.round(mins / 60);
  if (hours < 24) return `${hours} h ago`;
  const days = Math.round(hours / 24);
  return days === 1 ? 'yesterday' : `${days} days ago`;
}

/**
 * Client-side mirror of the proxy's ask-panelists channel validation:
 * "#lowercase-name" (public channel) or a raw channel id (C…), max 80 chars,
 * whitespace stripped by the caller.
 */
export function isValidSlackChannel(value: string): boolean {
  const v = value.replace(/\s+/g, '');
  if (!v || v.length > 80) return false;
  return /^#[a-z0-9._-]+$/.test(v) || /^C[A-Z0-9]{8,}$/.test(v);
}

/** Top 3 slots for holds / panel outreach — free first, then needs-move. */
export function topSlots(slots: GradedSlot[], n = 3): GradedSlot[] {
  const free = slots.filter((s) => s.grade === 'free');
  const move = slots.filter((s) => s.grade === 'needs-move');
  return [...free, ...move].slice(0, n);
}

/** "14:30" in Berlin time — the start options inside a grouped free window. */
export function fmtTimeOnly(iso: string): string {
  return new Date(iso).toLocaleTimeString('en-GB', {
    timeZone: TZ,
    hour: '2-digit',
    minute: '2-digit',
  });
}

/** How many meetings would have to move for this slot. */
export const moveCountOf = (s: GradedSlot): number =>
  s.moveCount ?? (s.conflicts?.length ?? 0);

/**
 * Display order for a slot group: 🟢 free first, then 🟡 needs-move, then 🔁
 * fallback options.
 *
 * Free and fallback stay chronological (the server grades chronologically, so a
 * stable filter per grade is enough). 🟡 needs-move is sorted by HOW MANY
 * MEETINGS would have to move, fewest first, ties chronological — the cost of a
 * yellow slot is the disruption it asks for, so the cheapest one has to be the
 * one the recruiter reads first (Theresa, Sep 2026). `sort` on a copy: the
 * caller's array is shared with the board payload.
 */
export function sortSlotsForDisplay(slots: GradedSlot[]): GradedSlot[] {
  const needsMove = slots
    .filter((s) => s.grade === 'needs-move')
    .map((s, i) => ({ s, i }))
    .sort((a, b) => moveCountOf(a.s) - moveCountOf(b.s) || a.i - b.i)
    .map((e) => e.s);
  return [
    ...slots.filter((s) => s.grade === 'free'),
    ...needsMove,
    ...slots.filter((s) => s.grade === 'fallback'),
  ];
}

/**
 * Does this slot answer to `start`? A grouped free window offers several starts
 * (`startOptions`) and the recruiter may have ticked any of them, so its own
 * `start` is not the only match.
 */
export const slotOffersStart = (s: GradedSlot, start: string): boolean =>
  s.start === start || (s.startOptions ?? []).includes(start);

/**
 * The slots the "Propose times" modal may offer, in display order.
 *
 * EVERY slot the recruiter ticked in the panel section comes first and is never
 * dropped, then the standard suggestions fill the rest. It used to be
 * `topSlots(slots, 3)` with no knowledge of the selection at all, so a
 * recruiter who ticked five slots got a modal listing three unrelated ones with
 * only the first pre-checked — and the candidate was emailed times nobody had
 * chosen (Theresa, Sep 2026).
 */
export function proposableSlots(slots: GradedSlot[], preselected: string[] = []): GradedSlot[] {
  const picked = slots.filter((s) => preselected.some((start) => slotOffersStart(s, start)));
  const pickedStarts = new Set(picked.map((s) => s.start));
  const fill = topSlots(
    slots.filter((s) => !pickedStarts.has(s.start)),
    Math.max(0, 3 - picked.length)
  );
  return sortSlotsForDisplay([...picked, ...fill]);
}

/**
 * The slots the board actively suggests, in display order. The server marks
 * them (`suggested`: candidates waiting + 2, free before single-move, lunch-only
 * and multi-move last-resort). Boards cached before the flag existed fall back
 * to the old top-5 free-then-move pick so the list never comes up empty.
 */
export function suggestedSlots(slots: GradedSlot[]): GradedSlot[] {
  const flagged = slots.filter((s) => s.suggested);
  return sortSlotsForDisplay(flagged.length > 0 ? flagged : topSlots(slots, 5));
}

// ───────────────────────────── 🏁 FINAL ROUNDS ─────────────────────────────

/**
 * A chain option viewed as a graded slot — lets the Mark-proposed flow reuse
 * the existing multi-select body (fmtSlotRange etc. only need start/end).
 */
export function chainAsSlot(chain: ChainOption): GradedSlot {
  return {
    start: chain.start,
    end: chain.end,
    grade: chain.grade,
    conflicts: chain.conflicts.map(({ email, title }) => ({ email, title })),
  };
}

/**
 * Client-side mirror of the proxy's TRACKING_STATUS map — what a tracking kind
 * does to the status badge, for optimistic updates.
 */
export const TRACKING_KIND_STATUS: Record<TrackingKind, string> = {
  asked: 'asked',
  ready: 'ready to schedule',
  offered: 'offered',
  confirmed: 'confirmed',
  blocked: 'blocked',
  withdrawn: 'ready to schedule',
  'fr-proposed': 'final round proposed',
  'fr-confirmed': 'final round confirmed',
  'fr-booked': 'final round booked',
  'fr-debrief-done': 'debrief done',
  'fr-withdrawn': 'final round to schedule',
};

/**
 * Server drafts are plain TEXT; the editor is contentEditable, so it needs HTML.
 *
 * Escape first, then linkify, then turn newlines into breaks. Order matters: escaping after
 * linkifying would destroy the anchors, and linkifying without escaping would let text become
 * markup. Bare URLs become real <a> elements because a candidate should get a clickable careers
 * link, not a string they have to copy — and because the proxy's sanitizer allowlists exactly
 * <a href="http|https|mailto">, so these survive the round trip on save.
 */
export function toEditableHtml(text: string): string {
  const escaped = text
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;');
  // Trailing punctuation is excluded from the href so "see finn.com/careers." does not link the dot.
  const linked = escaped.replace(
    /(https?:\/\/[^\s<]+[^\s<.,;:!?)\]}])/g,
    '<a href="$1">$1</a>'
  );
  return linked.replace(/\n/g, '<br>');
}

/**
 * 🟢 free first, then 🟡 by HOW MANY MEETINGS would have to move, fewest first,
 * ties chronological.
 *
 * The solver ranks by conflicts while picking options but returns them in
 * chronological order, so a Tuesday needing three moves was offered above a
 * Thursday needing one. What makes a yellow option expensive is the disruption
 * it asks of the panel, so the cheapest one has to read first (Theresa, Sep
 * 2026). Sorted on a copy — `chains` belongs to the board payload.
 */
export function sortChainsForDisplay(chains: ChainOption[]): ChainOption[] {
  return chains
    .map((c, i) => ({ c, i }))
    .sort(
      (a, b) =>
        Number(a.c.grade !== 'free') - Number(b.c.grade !== 'free') ||
        a.c.conflicts.length - b.c.conflicts.length ||
        a.i - b.i
    )
    .map((e) => e.c);
}

/**
 * "45 min each" or "final 45 min, bar raiser 30 min".
 *
 * The two seats have separate lengths now, and "each" was a claim about the
 * template that stopped being true the first time a role ran 45 + 30.
 */
export function roundLengthLabel(config: { interviewMinutes: number; barRaiserMinutes?: number }): string {
  const bar = config.barRaiserMinutes;
  return bar && bar !== config.interviewMinutes
    ? `final ${config.interviewMinutes} min, bar raiser ${bar} min`
    : `${config.interviewMinutes} min each`;
}

/** One seat on a slot's panel, and whether it is a stand-in. */
export interface SeatedInterviewer {
  email: string;
  /** Set when this person replaces a regular panelist on THIS slot. */
  steppedInFor?: string;
}

/**
 * Who would actually run the interview at a given slot.
 *
 * The role's panel is the default, but a fallback-tier slot swaps one person
 * out — and until now that only showed as prose at the end of a truncated hint
 * ("Sofia steps in for Theresa"), so the constellation for each date option was
 * not something a recruiter could see at a glance (Theresa, Sep 2026).
 *
 * A needs-move slot keeps the REGULAR panel: its `fallbackOption` is an
 * alternative the recruiter may take, not the seating as it stands, and showing
 * the stand-in there would claim a swap nobody has chosen.
 */
export function seatedPanelFor(
  slot: Pick<GradedSlot, 'grade' | 'replaced'>,
  interviewers: string[] | null | undefined
): SeatedInterviewer[] {
  const base = (interviewers || []).filter(Boolean);
  const swap = slot.grade === 'fallback' ? slot.replaced : null;
  if (!swap?.in) return base.map((email) => ({ email }));

  const out = swap.out?.toLowerCase();
  let replaced = false;
  const seats = base.map((email) => {
    if (!replaced && email.toLowerCase() === out) {
      replaced = true;
      return { email: swap.in, steppedInFor: swap.out };
    }
    return { email };
  });
  // The person stepping in must always appear. If the panel list does not
  // contain whoever they replace — a stale board payload, a panel edited since
  // the grading — appending is the honest answer: naming the wrong people is
  // worse than naming one extra.
  return replaced ? seats : [...seats, { email: swap.in, steppedInFor: swap.out }];
}
